/**
 * Parse a workbook into import tuples.
 *
 * Detection-based parsing: the parser finds the header row (or column) by
 * scanning for recognizable identifier labels, then derives orientation,
 * classifies columns, and extracts tuples from the data region.
 *
 * Handles:
 *   - Column-oriented sheets (identifiers down the left, periods across the top).
 *   - Row-oriented sheets (identifiers across the top, periods down the left).
 *     Internally we transpose so downstream logic is always column-oriented.
 *   - Extra rows above the header (title rows, spacers) — the header is whatever
 *     row contains 'scenario' and 'account' labels, not necessarily row 1.
 *   - Unrecognized columns (e.g. "Notes") between identifiers and periods —
 *     these are tracked separately and do not produce tuples.
 *
 * Returns { tuples, errors, warnings, info, ignored }:
 *   tuples   — ready to POST to /import/validate or /import/execute
 *   errors   — blocking issues (missing headers, missing identifiers, bad numbers)
 *   warnings — non-blocking (row with identifiers but no values)
 *   info     — structural detection info (orientation, header location, columns)
 *   ignored  — columns and rows the parser deliberately skipped, for display
 */

import { getSheetData } from './spreadsheet';
import { IMPORT_ERROR_CODES, IMPORT_WARNING_CODES } from './importErrorCodes';

const SCENARIO_HEADERS = new Set(['scenario', 'scenarioname', 'scenario_name']);
const ACCOUNT_HEADERS = new Set(['account', 'accountnumber', 'account_number']);
const ENTITY_HEADERS = new Set(['entity', 'entityname', 'entity_name']);

const normalizeHeader = (v) => {
    if (v === undefined || v === null) return '';
    return String(v).trim().toLowerCase().replace(/\s+/g, '');
};

const isBlank = (v) => v === undefined || v === null || v === '';

const normalizeString = (v) => {
    if (v === undefined || v === null) return '';
    return String(v).trim();
};

const parseNumericCell = (raw) => {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed === '') return new Error('blank');
        const hasComma = trimmed.includes(',');
        const hasDot = trimmed.includes('.');
        const normalized = hasComma && !hasDot ? trimmed.replace(',', '.') : trimmed;
        const n = Number(normalized);
        if (Number.isFinite(n)) return n;
    }
    return new Error('not a number');
};

/**
 * Classify a single header cell.
 * Returns 'scenario' | 'account' | 'entity' | 'unknown' based on whitelists.
 */
const headerRole = (raw) => {
    const n = normalizeHeader(raw);
    if (!n) return 'blank';
    if (SCENARIO_HEADERS.has(n)) return 'scenario';
    if (ACCOUNT_HEADERS.has(n)) return 'account';
    if (ENTITY_HEADERS.has(n)) return 'entity';
    return 'unknown';
};

/**
 * Count identifier labels in a row (or column).
 * Returns the number of cells whose content is 'scenario', 'account', or 'entity'.
 */
const countIdentifiers = (cells) => {
    let count = 0;
    for (const c of cells) {
        const role = headerRole(c);
        if (role === 'scenario' || role === 'account' || role === 'entity') count++;
    }
    return count;
};

/**
 * Transpose a 2D array.
 * Handles ragged rows by padding with undefined up to the longest row.
 */
const transpose = (grid) => {
    if (!grid || !grid.length) return [];
    const maxLen = grid.reduce((m, row) => Math.max(m, row?.length || 0), 0);
    const out = [];
    for (let c = 0; c < maxLen; c++) {
        const newRow = [];
        for (let r = 0; r < grid.length; r++) {
            newRow.push(grid[r]?.[c]);
        }
        out.push(newRow);
    }
    return out;
};

/**
 * Detect the header's location and the sheet's orientation.
 *
 * Scans every row and every column for identifier labels. The axis with the
 * most matches (and at least 2) wins. If neither axis qualifies, falls back
 * to row 0 with a flag indicating weak detection.
 *
 * Returns { orientation, headerIndex, confidence }:
 *   orientation:  'columns' (periods run across) or 'rows' (periods run down)
 *   headerIndex:  the row or column index of the header
 *   confidence:   number of identifier matches on the winning axis (0 if weak)
 */
const detectStructure = (grid) => {
    if (!grid || !grid.length) {
        return { orientation: 'columns', headerIndex: 0, confidence: 0 };
    }

    // Score each row
    let bestRow = { index: 0, count: 0 };
    for (let r = 0; r < grid.length; r++) {
        const count = countIdentifiers(grid[r] || []);
        if (count > bestRow.count) bestRow = { index: r, count };
    }

    // Score each column
    const maxCols = grid.reduce((m, row) => Math.max(m, row?.length || 0), 0);
    let bestCol = { index: 0, count: 0 };
    for (let c = 0; c < maxCols; c++) {
        const columnCells = grid.map((row) => row?.[c]);
        const count = countIdentifiers(columnCells);
        if (count > bestCol.count) bestCol = { index: c, count };
    }

    // Require at least 2 identifier matches to commit to an axis.
    // If neither qualifies, default to columns-orientation with header in row 0
    // and let downstream "missing headers" check fire.
    const rowQualifies = bestRow.count >= 2;
    const colQualifies = bestCol.count >= 2;

    if (!rowQualifies && !colQualifies) {
        return { orientation: 'columns', headerIndex: 0, confidence: 0 };
    }

    // Both qualify — prefer rows (most common case).
    if (bestRow.count >= bestCol.count) {
        return { orientation: 'columns', headerIndex: bestRow.index, confidence: bestRow.count };
    }
    return { orientation: 'rows', headerIndex: bestCol.index, confidence: bestCol.count };
};

/**
 * Classify a header row into identifier columns, period columns, and unrecognized ones.
 */
const classifyHeaders = (headerRow) => {
    let scenarioCol = null;
    let accountCol = null;
    let entityCol = null;
    const periodColumns = [];
    const unrecognizedColumns = [];

    for (let col = 0; col < headerRow.length; col++) {
        const raw = headerRow[col];
        const role = headerRole(raw);
        const displayName = normalizeString(raw);

        if (role === 'scenario' && scenarioCol === null) {
            scenarioCol = col;
        } else if (role === 'account' && accountCol === null) {
            accountCol = col;
        } else if (role === 'entity' && entityCol === null) {
            entityCol = col;
        } else if (role === 'blank') {
            // Blank header — skip entirely, not tracked
        } else {
            // role === 'unknown' OR duplicate identifier: treat as period initially.
            // Backend will later tell us if it's not a real period; at that point
            // the frontend reclassifies typo suggestions as errors and the rest
            // as silently ignored.
            periodColumns.push({ col, period: displayName });
        }
    }

    return { scenarioCol, accountCol, entityCol, periodColumns, unrecognizedColumns };
};

export const parseSheetToTuples = (workbook, sheetIndex = 0) => {
    const rawSheet = getSheetData(workbook, false, sheetIndex);

    const tuples = [];
    const errors = [];
    const warnings = [];
    const ignored = {
        rowsAboveHeader: 0,    // rows skipped because they're above the detected header
        unrecognizedColumns: [], // populated after backend validation, not at parse time
    };
    const info = {
        orientation: 'columns',
        headerIndex: 0,
        scenarioColumn: null,
        accountColumn: null,
        entityColumn: null,
        periodColumns: [],
    };

    if (!rawSheet || !rawSheet.length) {
        return { tuples, errors, warnings, info, ignored };
    }

    // 1. Detect structure
    const { orientation, headerIndex, confidence } = detectStructure(rawSheet);

    // 2. Normalize to columns-orientation by transposing if needed
    const grid = orientation === 'rows' ? transpose(rawSheet) : rawSheet;

    info.orientation = orientation;
    info.headerIndex = headerIndex + 1; // 1-indexed for display
    ignored.rowsAboveHeader = headerIndex;

    // 3. Extract and classify the header
    const headerRow = grid[headerIndex] || [];
    const { scenarioCol, accountCol, entityCol, periodColumns } = classifyHeaders(headerRow);

    info.scenarioColumn = scenarioCol != null ? scenarioCol + 1 : null;
    info.accountColumn = accountCol != null ? accountCol + 1 : null;
    info.entityColumn = entityCol != null ? entityCol + 1 : null;
    info.periodColumns = periodColumns.map((pc) => pc.period);

    // 4. Required headers check
    const missing = [];
    if (scenarioCol === null) missing.push('scenario');
    if (accountCol === null) missing.push('account');
    if (missing.length || confidence < 2) {
        errors.push({
            row: info.headerIndex,
            col: null,
            code: IMPORT_ERROR_CODES.MISSING_HEADERS,
            details: { missing: missing.length ? missing : ['scenario', 'account'] },
        });
        return { tuples, errors, warnings, info, ignored };
    }

    // 5. Extract tuples from rows after the header
    for (let rowIdx = headerIndex + 1; rowIdx < grid.length; rowIdx++) {
        const row = grid[rowIdx] || [];

        // For display purposes, we want the "original" row number — i.e. as it
        // appears in the user's spreadsheet. For column-orientation that's rowIdx+1;
        // for row-orientation (transposed) that's the original column, which is rowIdx+1
        // in the transposed grid. Either way, rowIdx+1 is a reasonable display index
        // AFTER noting orientation, since the preview will clarify.
        const displayRow = rowIdx + 1;

        const scenarioName = normalizeString(row[scenarioCol]);
        const accountNumber = normalizeString(row[accountCol]);

        const identifiersBlank = !scenarioName && !accountNumber;
        const valuesAllBlank = periodColumns.every(({ col }) => isBlank(row[col]));

        if (identifiersBlank && valuesAllBlank) continue;

        if (!scenarioName || !accountNumber) {
            errors.push({
                row: displayRow,
                col: null,
                code: IMPORT_ERROR_CODES.MISSING_IDENTIFIERS,
            });
            continue;
        }

        if (valuesAllBlank) {
            warnings.push({
                row: displayRow,
                col: null,
                code: IMPORT_WARNING_CODES.BLANK_ROW_WITH_IDENTIFIERS,
            });
            continue;
        }

        for (const { col, period } of periodColumns) {
            const raw = row[col];
            if (isBlank(raw)) continue;

            const parsed = parseNumericCell(raw);
            if (parsed instanceof Error) {
                errors.push({
                    row: displayRow,
                    col: col + 1,
                    code: IMPORT_ERROR_CODES.INVALID_NUMBER,
                    value: String(raw),
                });
                continue;
            }

            tuples.push({
                scenarioName,
                accountNumber,
                period,
                value: parsed,
                _row: displayRow,
                _col: col + 1,
            });
        }
    }

    return { tuples, errors, warnings, info, ignored };
};